/**
 * 角色 × 功能矩陣（工作區收斂）
 *
 * 能力語意：
 * - view：可見選單／列表
 * - write：可寫入／狀態異動
 * - sensitive：可見身分證／生日等敏感欄（僅管理視角）
 * - password：可更改密碼（業務僅 partner；管理視角全身份；行政無）
 *
 * 選單原則：共用模組維持獨立權限碼；側欄用工作區捷徑／葉頁，
 * 父層閘門只用「工作區入口碼」，勿把共用葉碼掛進部門父層。
 */

/** @typedef {'none'|'view'|'write'} AccessLevel */

export const WORKSPACE = {
    PARTNER_OPS: 'partnerOps',
    USER_ADMIN: 'userAdmin',
    SYSTEM_OPS: 'systemOps',
    PLATFORM: 'platform',
    ORDER_DESK: 'orderDesk',
    SHOP_CATALOG: 'shopCatalog',
    POINTS: 'points',
    LEVELS: 'levels'
};

/** 側欄父層：僅這些碼可打開對應工作區（不含共用葉碼外溢） */
export const WORKSPACE_ENTRY_CODES = {
    [WORKSPACE.PARTNER_OPS]: [
        '210000',
        '100118',
        '100119',
        '211000',
        '211001',
        '211002',
        '211003',
        '211004',
        '211005',
        '211006',
        '211011',
        '211012',
        '211013',
        '212000',
        '212001',
        '212002',
        '213000',
        '213001',
        '213002',
        '214000',
        '214001',
        '900101',
        '900102',
        '900103',
        '900104',
        '900201',
        '900202',
        '900203',
        '900204'
    ],
    [WORKSPACE.SYSTEM_OPS]: [
        '100000',
        '900300',
        '900301',
        '900302',
        '900303',
        '900304',
        // 原平台管理（管理員／角色／財務串接）；稽核 200xxx 獨立於操作紀錄父層
        '990000',
        '990101',
        '990102',
        '990103',
        '990104',
        '990201',
        '990202',
        '990203',
        '990204',
        '990205',
        '900400',
        '900401',
        '900402',
        '900800',
        '900801',
        // 修業進路
        '900500',
        '900501',
        '900502',
        '900503',
        '900504',
        '900505',
        '100121',
        '100130',
        '100131',
        '100132',
        '100133',
        // 背包物品類型／服務白名單（前台串接閘門）
        '190700',
        '190701',
        '190702',
        '190703',
        '190704'
    ],
    [WORKSPACE.USER_ADMIN]: ['100106'],
    /** @deprecated 選單已併入 SYSTEM_OPS；保留入口碼供舊角色矩陣／測試 */
    [WORKSPACE.PLATFORM]: [
        '990000',
        '990101',
        '990102',
        '990103',
        '990104',
        '990201',
        '990202',
        '990203',
        '990204',
        '990205',
        '900400',
        '900401',
        '900402',
        '900800',
        '900801'
    ],
    [WORKSPACE.ORDER_DESK]: [
        '150300',
        '150301',
        '150302',
        '150303',
        '150304',
        '150305',
        '150306',
        '150500',
        '150501',
        '150502',
        '150503',
        '150504',
        // 櫃台兌換核銷（背包模組葉碼；行政可單獨勾選）
        '190900',
        '190901'
    ],
    [WORKSPACE.SHOP_CATALOG]: [
        '150000',
        '150100',
        '150101',
        '150102',
        '150103',
        '150104',
        '150200',
        '150201',
        '150202',
        '150203',
        '150204',
        '150205',
        '150400',
        '150401',
        '150402',
        '150403'
    ],
    [WORKSPACE.POINTS]: ['110000', '110100', '110102', '110103', '110200'],
    [WORKSPACE.LEVELS]: [
        '120000',
        '120100',
        '120101',
        '120102',
        '120103',
        '120104',
        '120200'
    ]
};

/**
 * 第一波角色矩陣（規劃預設；實際以角色管理勾選為準）
 * memberView: business | generalAdmin | manager | none
 * orderDesk: full | finance | readonly | none
 */
export const ROLE_FEATURE_MATRIX = {
    business: {
        label: '業務',
        workspaces: [WORKSPACE.PARTNER_OPS, WORKSPACE.POINTS, WORKSPACE.LEVELS],
        member: {
            view: 'business',
            write: true,
            sensitive: true,
            password: 'partner_only'
        },
        shopCatalog: { access: 'none' },
        orderDesk: { access: 'none' },
        notes: '積分／等級維持頂層獨立，後續看狀況再開權'
    },
    generalAdmin: {
        label: '行政',
        workspaces: [WORKSPACE.USER_ADMIN],
        member: {
            view: 'generalAdmin',
            write: false,
            sensitive: false,
            password: 'none'
        },
        shopCatalog: { access: 'none' },
        orderDesk: { access: 'none' },
        notes: '用戶管理列表；無改密、無身分證／生日'
    },
    marketing: {
        label: '行銷',
        workspaces: [WORKSPACE.USER_ADMIN, WORKSPACE.ORDER_DESK],
        member: {
            view: 'generalAdmin',
            write: false,
            sensitive: false,
            password: 'none'
        },
        shopCatalog: { access: 'none' },
        orderDesk: { access: 'readonly' },
        notes: '會員精簡視圖＋訂單唯讀工作面'
    },
    finance: {
        label: '財務',
        workspaces: [WORKSPACE.ORDER_DESK],
        member: {
            view: 'none',
            write: false,
            sensitive: false,
            password: 'none'
        },
        shopCatalog: { access: 'none' },
        orderDesk: { access: 'finance' },
        notes: '訂單／財務對帳／取消審核；不進商品 CRUD'
    },
    course: {
        label: '課程',
        workspaces: [WORKSPACE.ORDER_DESK],
        member: {
            view: 'none',
            write: false,
            sensitive: false,
            password: 'none'
        },
        shopCatalog: { access: 'none' },
        orderDesk: { access: 'readonly' },
        notes: '第二波可再加課程相關表單；訂單工作面與行銷類似'
    },
    system: {
        label: '系統／管理者',
        workspaces: [
            WORKSPACE.SYSTEM_OPS,
            WORKSPACE.SHOP_CATALOG,
            WORKSPACE.ORDER_DESK,
            WORKSPACE.POINTS,
            WORKSPACE.LEVELS
        ],
        member: {
            view: 'manager',
            write: true,
            sensitive: true,
            password: 'all'
        },
        shopCatalog: { access: 'write' },
        orderDesk: { access: 'full' },
        notes: '系統營運（含原平台管理）＋完整商城；操作紀錄另依 200xxx'
    }
};
